import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import toast from 'react-hot-toast';
import SSOButtons, { SSO_ENABLED } from '../components/SSOButtons.jsx';

// ── Multi-sport data ──────────────────────────────────────────────────────────
const SPORTS = [
  { icon: '🏒', name: 'Hockey',      color: '#00d4ff' },
  { icon: '⚽', name: 'Soccer',      color: '#00ff88' },
  { icon: '🏀', name: 'Basketball',  color: '#ff9500' },
  { icon: '🏈', name: 'Football',    color: '#ff6b35' },
  { icon: '⚾', name: 'Baseball',    color: '#ff4488' },
  { icon: '🎾', name: 'Tennis',      color: '#c8ff00' },
  { icon: '🏐', name: 'Volleyball',  color: '#aa88ff' },
  { icon: '🏑', name: 'Field Hockey',color: '#00ffcc' },
];

const FEATURES = [
  { icon: '📅', text: 'Schedule games & practices' },
  { icon: '✅', text: 'RSVP & spare management' },
  { icon: '⚖️', text: 'AI-powered team balancing' },
  { icon: '💬', text: 'Team chat & notifications' },
  { icon: '📊', text: 'Stats, ratings & analytics' },
];

// ── Abstract multi-sport field SVG ────────────────────────────────────────────
function SportFieldArt() {
  return (
    <svg viewBox="0 0 400 520" xmlns="http://www.w3.org/2000/svg"
      style={{ width: '100%', height: '100%', position: 'absolute', inset: 0 }}>
      <defs>
        <linearGradient id="field-grad-v" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#00d4ff" stopOpacity="0.9" />
          <stop offset="50%"  stopColor="#7c3aed" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#00ff88" stopOpacity="0.8" />
        </linearGradient>
        <linearGradient id="field-grad-h" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="#00d4ff" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#00ff88" stopOpacity="0.4" />
        </linearGradient>
        <radialGradient id="field-glow" cx="50%" cy="50%">
          <stop offset="0%"   stopColor="#7c3aed" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#00d4ff" stopOpacity="0" />
        </radialGradient>
        <filter id="field-blur">
          <feGaussianBlur stdDeviation="1.2" />
        </filter>
      </defs>

      {/* Soft background glow */}
      <ellipse cx="200" cy="260" rx="180" ry="230" fill="url(#field-glow)" />

      {/* ── Outer arena/oval boundary ─── */}
      <rect x="32" y="44" width="336" height="432" rx="64"
        fill="none" stroke="url(#field-grad-v)" strokeWidth="1.8" opacity="0.7" />

      {/* ── Inner playing surface ─────── */}
      <rect x="72" y="100" width="256" height="320" rx="12"
        fill="none" stroke="url(#field-grad-h)" strokeWidth="1.2" opacity="0.65" />

      {/* ── Midfield line ─────────────── */}
      <line x1="72" y1="260" x2="328" y2="260"
        stroke="url(#field-grad-h)" strokeWidth="1" opacity="0.7" />

      {/* ── Centre circle ─────────────── */}
      <circle cx="200" cy="260" r="52"
        fill="none" stroke="url(#field-grad-v)" strokeWidth="1.2" opacity="0.75" />

      {/* ── Centre spot ───────────────── */}
      <circle cx="200" cy="260" r="5"
        fill="url(#field-grad-v)" opacity="0.9" />

      {/* ── Top goal / crease area ────── */}
      <rect x="148" y="100" width="104" height="56" rx="6"
        fill="none" stroke="url(#field-grad-h)" strokeWidth="1" opacity="0.6" />
      {/* top crease arc */}
      <path d="M 168 156 A 32 24 0 0 1 232 156"
        fill="none" stroke="#00d4ff" strokeWidth="0.9" opacity="0.55" />

      {/* ── Bottom goal / crease area ─── */}
      <rect x="148" y="364" width="104" height="56" rx="6"
        fill="none" stroke="url(#field-grad-h)" strokeWidth="1" opacity="0.6" />
      {/* bottom crease arc */}
      <path d="M 168 364 A 32 24 0 0 0 232 364"
        fill="none" stroke="#00ff88" strokeWidth="0.9" opacity="0.55" />

      {/* ── Penalty spots ─────────────── */}
      <circle cx="200" cy="176" r="3.5" fill="#00d4ff" opacity="0.7" />
      <circle cx="200" cy="344" r="3.5" fill="#00ff88" opacity="0.7" />

      {/* ── Corner face-off circles ───── */}
      {[[108,156],[292,156],[108,364],[292,364]].map(([cx,cy],i) => (
        <circle key={i} cx={cx} cy={cy} r="26"
          fill="none" stroke="url(#field-grad-v)" strokeWidth="0.8" opacity="0.45" />
      ))}

      {/* ── Three-point / side arcs ────── */}
      <path d="M 72 160 Q 56 260 72 360"
        fill="none" stroke="url(#field-grad-v)" strokeWidth="0.7" opacity="0.35" />
      <path d="M 328 160 Q 344 260 328 360"
        fill="none" stroke="url(#field-grad-v)" strokeWidth="0.7" opacity="0.35" />

      {/* ── Hash marks / lane dots ─────── */}
      {[210,230,250,270,290,310].map((y,i) => (
        <line key={i} x1="155" y1={y} x2="165" y2={y}
          stroke="#00d4ff" strokeWidth="0.7" opacity="0.3" />
      ))}
      {[210,230,250,270,290,310].map((y,i) => (
        <line key={i} x1="235" y1={y} x2="245" y2={y}
          stroke="#00d4ff" strokeWidth="0.7" opacity="0.3" />
      ))}

      {/* ── Subtle blurred outer ring ──── */}
      <rect x="18" y="30" width="364" height="460" rx="76"
        fill="none" stroke="url(#field-grad-v)" strokeWidth="3"
        opacity="0.12" filter="url(#field-blur)" />
    </svg>
  );
}

// ── Login page ────────────────────────────────────────────────────────────────
export default function LoginPage() {
  const { login } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  // v2.5.174 — when RegisterPage bounces an already-registered user here
  // (they clicked a new invite and tried to create a duplicate account),
  // it passes their email + a flag in navigation state. Prefill the email
  // and show a one-line heads-up so the handoff feels intentional, not
  // like they hit an error wall.
  const fromDuplicateRegister = !!location.state?.fromDuplicateRegister;
  const [form, setForm]       = useState({ email: location.state?.email || '', password: '' });
  const [loading, setLoading] = useState(false);
  const [activeSport, setActiveSport] = useState(0);

  // Cycle through sport names in the tagline
  useEffect(() => {
    const t = setInterval(() => setActiveSport(s => (s + 1) % SPORTS.length), 2200);
    return () => clearInterval(t);
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate('/dashboard');
    } catch (err) {
      // Distinguish auth failures from post-login invite-accept failures.
      // The former means "your password is wrong" — stay on login page.
      // The latter means "you logged in fine, but the invite couldn't
      // be applied" — show the explanation and send them onward to
      // /dashboard anyway (they ARE logged in, just not on the team).
      if (err.isInviteFailure) {
        toast.error(err.message, { duration: 6000 });
        navigate('/dashboard');
      } else {
        toast.error(err.response?.data?.error || 'Login failed');
      }
    }
    setLoading(false);
  };

  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const current = SPORTS[activeSport];

  return (
    <>
      {/* Float animation for sport icons */}
      <style>{`
        @keyframes gd-float {
          0%   { transform: translateY(0px) scale(1); }
          100% { transform: translateY(-6px) scale(1.05); }
        }
        @keyframes gd-sport-in {
          0%   { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes gd-pulse-ring {
          0%   { transform: scale(1);   opacity: 0.6; }
          100% { transform: scale(1.5); opacity: 0; }
        }
      `}</style>

      <div className="min-h-screen flex" style={{ background: 'var(--bg)' }}>

        {/* ── Left atmospheric panel ────────────────────────────────────────── */}
        <div className="hidden lg:flex flex-col justify-between relative overflow-hidden"
          style={{
            width: 440, flexShrink: 0,
            background: 'linear-gradient(160deg, #0a0f1e 0%, #0d1528 40%, #0e1630 100%)',
            borderRight: '1px solid rgba(0,212,255,0.12)',
          }}>

          {/* Field art — vivid, well-visible */}
          <div style={{ position: 'absolute', inset: 0, opacity: 0.52, pointerEvents: 'none' }}>
            <SportFieldArt />
          </div>

          {/* Ambient colour wash */}
          <div style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: 'radial-gradient(ellipse 70% 55% at 30% 45%, rgba(124,58,237,0.18) 0%, transparent 65%), radial-gradient(ellipse 50% 40% at 75% 70%, rgba(0,212,255,0.1) 0%, transparent 60%)',
          }} />

          {/* ── Top: logo + sport icons ──────────────────────────────────── */}
          <div style={{ position: 'relative', zIndex: 2, padding: '44px 40px 0' }}>
            {/* Logo */}
            <div style={{ marginBottom: 32 }}>
              <div style={{
                fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 48,
                letterSpacing: '0.06em', textTransform: 'uppercase', lineHeight: 0.88,
                color: '#ffffff',
                textShadow: '0 0 40px rgba(0,212,255,0.4)',
              }}>GAME<br/>DAY</div>
              <div style={{
                marginTop: 10, fontFamily: 'var(--font-display)', fontSize: 9.5,
                letterSpacing: '0.3em', color: 'rgba(0,212,255,0.8)', textTransform: 'uppercase',
              }}>TEAM MANAGEMENT · ALL SPORTS</div>
            </div>

            {/* Sport icon grid */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {SPORTS.map((sport, i) => {
                const isActive = activeSport === i;
                return (
                  <div key={sport.name}
                    title={sport.name}
                    style={{
                      position: 'relative',
                      width: 46, height: 46, borderRadius: 13,
                      background: isActive ? `${sport.color}22` : 'rgba(255,255,255,0.04)',
                      border: `1.5px solid ${isActive ? sport.color + '60' : 'rgba(255,255,255,0.08)'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 22,
                      transition: 'all 0.4s cubic-bezier(0.34,1.56,0.64,1)',
                      transform: isActive ? 'scale(1.15)' : 'scale(1)',
                      cursor: 'default',
                      animation: `gd-float ${2.2 + i * 0.25}s ease-in-out ${i * 0.12}s infinite alternate`,
                      boxShadow: isActive ? `0 0 16px ${sport.color}35` : 'none',
                    }}>
                    {sport.icon}
                    {/* Pulse ring on active */}
                    {isActive && (
                      <div style={{
                        position: 'absolute', inset: -6, borderRadius: 19,
                        border: `1.5px solid ${sport.color}`,
                        animation: 'gd-pulse-ring 1.1s ease-out infinite',
                        pointerEvents: 'none',
                      }} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Bottom: tagline + features ───────────────────────────────── */}
          <div style={{ position: 'relative', zIndex: 2, padding: '0 40px 44px' }}>

            {/* Cycling sport name tagline */}
            <div style={{ marginBottom: 28 }}>
              <div style={{
                fontFamily: 'var(--font-display)', fontWeight: 800, fontStyle: 'italic',
                fontSize: 34, lineHeight: 1.1, color: '#ffffff',
                textShadow: '0 2px 20px rgba(0,0,0,0.5)',
                marginBottom: 6,
              }}>
                Your{' '}
                <span key={activeSport} style={{
                  color: current.color,
                  textShadow: `0 0 20px ${current.color}60`,
                  display: 'inline-block',
                  animation: 'gd-sport-in 0.35s ease-out forwards',
                }}>
                  {current.name}
                </span>
                {' '}team,<br />perfectly managed.
              </div>
            </div>

            {/* Thin divider */}
            <div style={{
              height: 1, marginBottom: 20,
              background: `linear-gradient(to right, ${current.color}60, transparent)`,
              transition: 'background 0.4s',
            }} />

            {/* Feature list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {FEATURES.map((f, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  fontSize: 12.5, color: 'rgba(255,255,255,0.65)',
                  letterSpacing: '0.02em', lineHeight: 1.4,
                }}>
                  <span style={{ fontSize: 15, flexShrink: 0 }}>{f.icon}</span>
                  {f.text}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Right: auth form ──────────────────────────────────────────────── */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 animate-fade-in">

          {/* Mobile logo */}
          <div className="lg:hidden mb-10 text-center">
            <div style={{
              fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 36,
              letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text)',
            }}>🏟 Game Day</div>
            <div style={{ fontSize: 11, color: 'var(--text-2)', letterSpacing: '0.15em', marginTop: 4 }}>
              TEAM MANAGEMENT · ALL SPORTS
            </div>
          </div>

          <div style={{ width: '100%', maxWidth: 380 }}>
            <div className="mb-8">
              <h1 style={{
                fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 32,
                letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text)',
                lineHeight: 1, marginBottom: 8,
              }}>Welcome Back</h1>
              <p style={{ color: 'var(--text-2)', fontSize: 14, lineHeight: 1.5 }}>
                Sign in to manage your team
              </p>
            </div>

            {fromDuplicateRegister && (
              <div style={{
                padding: '10px 14px', borderRadius: 10, marginBottom: 16,
                background: 'var(--ice-a3)', border: '1px solid var(--ice-a25)',
                fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5, textAlign: 'left',
              }}>
                <strong style={{ color: 'var(--ice)' }}>You already have an account.</strong>{' '}
                Sign in below and we'll add you to the team automatically.
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="label">Email</label>
                <input type="email" className="input-field" placeholder="you@email.com"
                  value={form.email} onChange={set('email')} required autoComplete="email" />
              </div>
              <div>
                <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
                  <label className="label" style={{ marginBottom: 0 }}>Password</label>
                  <Link to="/forgot-password" style={{
                    fontSize: 10, color: 'var(--ice)', letterSpacing: '0.1em',
                    fontFamily: 'var(--font-display)', fontWeight: 600,
                  }}>FORGOT?</Link>
                </div>
                <input type="password" className="input-field" placeholder="••••••••"
                  value={form.password} onChange={set('password')} required autoComplete="current-password" />
              </div>

              <button type="submit" disabled={loading} className="btn-ice w-full justify-center mt-2"
                style={{ padding: '13px 20px', fontSize: 14, letterSpacing: '0.1em' }}>
                {loading
                  ? <span className="spinner" style={{ width: 18, height: 18 }} />
                  : 'SIGN IN →'}
              </button>
            </form>

            {SSO_ENABLED && (<>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              <span style={{ fontSize: 11, color: 'var(--text-2)', letterSpacing: '0.1em', fontFamily: 'var(--font-display)' }}>OR</span>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            </div>

            <SSOButtons
              onSuccess={() => navigate('/dashboard')}
              onError={(msg) => toast.error(msg)}
            />
            </>)}

            <div className="ice-divider my-6" />

            <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-2)' }}>
              New to Game Day?{' '}
              <Link to="/register" style={{
                color: 'var(--ice)', fontWeight: 700,
                fontFamily: 'var(--font-display)', letterSpacing: '0.05em',
              }}>CREATE ACCOUNT</Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
